// src/pages/HelpCenter.tsx

import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import Footer from '../components/layout/Footer';
import Navigation from '../components/layout/Navigation';
import { Helmet } from 'react-helmet-async';
import ParticleBackground from '../components/layout/ParticleBackground';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideDownFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideUpFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const HelpCenterContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: transparent;
  color: var(--color-text);
  position: relative;
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 6rem auto 4rem;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 6rem 1.5rem;
  }
`;

const Title = styled.h2`
  color: var(--color-text);
  font-size: 3.5rem;
  margin-bottom: 3rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  position: relative;
  animation: ${slideDownFadeIn} 0.8s ease-out forwards;
  opacity: 0;
  font-family: 'Maragsa-Display', Helvetica, Arial, sans-serif;
  text-align: center;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 120px;
    height: 3px;
    background: linear-gradient(90deg, transparent, var(--color-primary), transparent);
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
    letter-spacing: 1px;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  padding-bottom: 56.25%; /* 16:9 Aspect Ratio */
  height: 0;
  overflow: hidden;
  width: 100%;
  margin-bottom: 3rem;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 3fr;
  gap: 3rem;
  margin-top: 2rem;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const Sidebar = styled.div`
  animation: ${slideUpFadeIn} 0.8s ease-out 0.3s forwards;
  opacity: 0;
  position: sticky;
  top: 20px;
  align-self: flex-start;

  @media (max-width: 1024px) {
    position: static;
  }
`;

const SidebarTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: var(--color-text);
  font-family: 'Maragsa-Display', Helvetica, Arial, sans-serif;
  border-bottom: 2px solid var(--color-border);
  padding-bottom: 0.5rem;
`;

const SidebarList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const SidebarItem = styled.li`
  margin-bottom: 0.5rem;
`;

const SidebarLink = styled.a`
  color: var(--color-text-secondary);
  text-decoration: none;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  display: block;
  transition: all 0.3s ease;
  font-family: 'Montserrat', Arial, sans-serif;
  font-weight: 500;
  border-left: 3px solid transparent;

  &:hover {
    color: var(--color-primary);
    background-color: var(--color-card-bg);
    border-left-color: var(--color-primary);
  }

  &.active {
    color: var(--color-primary);
    background-color: var(--color-card-bg);
    border-left-color: var(--color-primary);
  }
`;

const ContentArea = styled.div`
  animation: ${fadeIn} 0.8s ease-out 0.6s forwards;
  opacity: 0;
`;

const SectionCard = styled.div`
  background: var(--color-card-bg);
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--color-border);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  }
`;

const SectionTitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: var(--color-text);
  font-family: 'Maragsa-Display', Helvetica, Arial, sans-serif;
  display: flex;
  align-items: center;
  gap: 1rem;

  &::before {
    content: '';
    width: 4px;
    height: 24px;
    background: var(--color-primary);
    border-radius: 2px;
  }
`;

const StepCard = styled.div`
  background: var(--color-input-bg);
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border-left: 4px solid var(--color-primary);
`;

const StepTitle = styled.h4`
  font-size: 1.25rem;
  margin-bottom: 1rem;
  color: var(--color-text);
  font-family: 'Montserrat', Arial, sans-serif;
  font-weight: 600;
`;

const StepImage = styled.img`
  width: 100%;
  max-width: 400px;
  height: auto;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.3s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  margin-top: 1rem;

  &:hover {
    transform: scale(1.02);
  }
`;

const FeatureCard = styled.div`
  margin-bottom: 2rem;
`;

const FeatureTitle = styled.h4`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: var(--color-text);
  font-family: 'Maragsa-Display', Helvetica, Arial, sans-serif;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &::before {
    content: '•';
    color: var(--color-primary);
    font-size: 1.5rem;
  }
`;

const FeatureContent = styled.div`
  font-size: 1rem;
  line-height: 1.7;
  color: var(--color-text-secondary);
  font-family: 'Montserrat', Arial, sans-serif;

  ul {
    margin-top: 1rem;
    padding-left: 1.5rem;
    list-style-type: none;

    li {
      margin-bottom: 0.75rem;
      position: relative;
      padding-left: 1.5rem;

      &::before {
        content: '→';
        position: absolute;
        left: 0;
        color: var(--color-primary);
        font-weight: bold;
      }

      strong {
        color: var(--color-text);
        font-weight: 600;
      }
    }
  }
`;


const FullscreenImage = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;

  img {
    max-width: 90%;
    max-height: 90%;
    border-radius: 12px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  }
`;

interface HelpCenterProps {
  isLoggedIn: boolean;
  onLogout: () => void;
}

const HelpCenter: React.FC<HelpCenterProps> = ({ isLoggedIn, onLogout }) => {
  const [fullscreenImageSrc, setFullscreenImageSrc] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState('getting-started');

  const handleImageClick = (src: string) => {
    setFullscreenImageSrc(src);
  };

  const closeFullscreen = () => {
    setFullscreenImageSrc(null);
  };

  useEffect(() => {
    if (window.fbq) {
      window.fbq('track', 'ViewContent', { page: 'HelpCenter' });
    }
  }, []);

  const handleVideoInteraction = () => {
    if (window.fbq) {
      window.fbq('track', 'HelpVideoWatched', { page: 'HelpCenter' });
    }
  };


  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <HelpCenterContainer>
      <Helmet>
        <title>Help Center | RiskMaster Documentation</title>
        <meta
          name="description"
          content="Comprehensive documentation and support for RiskMaster. Learn how to install, configure, and use all features of our risk management platform."
        />
        <link rel="canonical" href="https://www.riskmaster.net/help-center" />
      </Helmet>
      <ParticleBackground />
      <Navigation activeSection="help-center" handleScroll={() => {}} isLoggedIn={isLoggedIn} onLogout={onLogout} />
      <ContentWrapper>
        <Title>Help Center</Title>
        
        <VideoContainer>
          <iframe
            src="https://www.youtube.com/embed/nY-M8KC93mg"
            title="RiskMaster Installation and Overview Guide"
            frameBorder="0"
            allowFullScreen
            onLoad={handleVideoInteraction}
          />
        </VideoContainer>

        <MainContent>
          <Sidebar>
            <SidebarTitle>Quick Navigation</SidebarTitle>
            <SidebarList>
              <SidebarItem>
                <SidebarLink 
                  href="#getting-started" 
                  className={activeSection === 'getting-started' ? 'active' : ''}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('getting-started');
                  }}
                >
                  Getting Started
                </SidebarLink>
              </SidebarItem>
              <SidebarItem>
                <SidebarLink 
                  href="#features" 
                  className={activeSection === 'features' ? 'active' : ''}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('features');
                  }}
                >
                  Features Guide
                </SidebarLink>
              </SidebarItem>
              <SidebarItem>
                <SidebarLink 
                  href="#troubleshooting" 
                  className={activeSection === 'troubleshooting' ? 'active' : ''}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('troubleshooting');
                  }}
                >
                  Troubleshooting
                </SidebarLink>
              </SidebarItem>
            </SidebarList>
          </Sidebar>

          <ContentArea>
            {/* Getting Started Section */}
            <SectionCard id="getting-started">
              <SectionTitle>Getting Started</SectionTitle>
              <p style={{ color: 'var(--color-text-secondary)', marginBottom: '2rem', fontFamily: 'Montserrat, Arial, sans-serif' }}>
                Welcome to RiskMaster! This guide will help you get started with installation and basic setup.
              </p>
              
              <FeatureCard>
                <FeatureTitle>Installation Guide</FeatureTitle>
                <FeatureContent>
                  <p>Follow our comprehensive video guide above to install RiskMaster on your NinjaTrader platform.</p>
                </FeatureContent>
              </FeatureCard>
            </SectionCard>

            {/* Features Guide Section */}
            <SectionCard id="features">
              <SectionTitle>Features Guide</SectionTitle>
              
              <FeatureCard>
                <FeatureTitle>Rules Tab</FeatureTitle>
                <FeatureContent>
                  <p>
                    The Rules Tab is where you define trading profiles with specific risk management features. These profiles
                    can then be assigned to accounts to ensure trading discipline.
                  </p>
                  <ul>
                    <li>
                      <strong>Max Allowed Contracts:</strong> Limits the maximum number of contracts per order, regardless of the order type.
                    </li>
                    <li>
                      <strong>Daily Trade Limit:</strong> Specify the maximum number of trades allowed per day.
                    </li>
                    <li>
                      <strong>Consecutive Loss Limit:</strong> Limit the number of consecutive losing trades.
                    </li>
                    <li>
                      <strong>Daily Loss Threshold:</strong> Set the maximum loss allowable for the day.
                    </li>
                    <li>
                      <strong>Daily Profit Target:</strong> Define a profit target for the day.
                    </li>
                    <li>
                      <strong>Single Trade Loss Limit:</strong> Automatically close trades if losses exceed the specified amount.
                    </li>
                    <li>
                      <strong>Single Trade Profit Target:</strong> Automatically close trades if profits exceed the specified amount.
                    </li>
                    <li>
                      <strong>Allowed Trading Days:</strong> Choose specific days of the week for trading.
                    </li>
                    <li>
                      <strong>Time Restriction Window:</strong> Set specific trading hours during which trading is allowed.
                    </li>
                  </ul>
                </FeatureContent>
              </FeatureCard>

              <FeatureCard>
                <FeatureTitle>Profiles Tab</FeatureTitle>
                <FeatureContent>
                  <p>
                    The Profiles Tab lets you manage your risk management profiles. Each profile is a collection of rules configured in the Rules Tab.
                  </p>
                  <ul>
                    <li><strong>View Profiles:</strong> See all existing profiles along with their configured rules.</li>
                    <li><strong>Edit Profiles:</strong> Modify an existing profile to update its settings.</li>
                    <li><strong>Remove Profiles:</strong> Delete profiles that are no longer needed.</li>
                    <li><strong>Assign Profiles:</strong> Apply a profile to a specific account to enforce its restrictions.</li>
                  </ul>
                </FeatureContent>
              </FeatureCard>

              <FeatureCard>
                <FeatureTitle>Accounts Tab</FeatureTitle>
                <FeatureContent>
                  <p>
                    The Accounts Tab provides an overview of all connected trading accounts and allows you to manage restrictions and profiles.
                  </p>
                  <ul>
                    <li><strong>Lock Account:</strong> Manually lock an account to prevent trading.</li>
                    <li><strong>Assign Profile:</strong> Assign a profile from the Profiles Tab to an account.</li>
                    <li><strong>Remove Restrictions:</strong> Lift restrictions from accounts using the Third-Party Email feature.</li>
                    <li><strong>Reset Account:</strong> Reset an account that has an assigned profile for the next trading day.</li>
                  </ul>
                </FeatureContent>
              </FeatureCard>

              <FeatureCard>
                <FeatureTitle>Settings Tab</FeatureTitle>
                <FeatureContent>
                  <p>
                    The Settings Tab is where you configure system settings and manage permissions.
                  </p>
                  <ul>
                    <li><strong>License Status:</strong> Displays whether your license is active or inactive.</li>
                    <li><strong>Assign Email:</strong> Add your email address for support purposes.</li>
                    <li><strong>Third-Party Email:</strong> Add a trusted email to grant permissions for managing restrictions.</li>
                  </ul>
                </FeatureContent>
              </FeatureCard>

              <FeatureCard>
                <FeatureTitle>Time Restriction Window vs. New Trading Day</FeatureTitle>
                <FeatureContent>
                  <p>
                    <strong>Time Restriction Window:</strong> Uses your local computer time to define specific trading hours.
                  </p>
                  <p>
                    <strong>New Trading Day:</strong> Based on Eastern Time (ET) and starts at 6:00 PM ET. Account restrictions reset automatically at the start of the new trading day.
                  </p>
                  <p>
                    <strong>Key Difference:</strong> Time Restriction Window enforces specific trading hours during the day based on local time, while New Trading Day determines when account resets occur and is always based on ET.
                  </p>
                </FeatureContent>
              </FeatureCard>
            </SectionCard>

            {/* Troubleshooting Section */}
            <SectionCard id="troubleshooting">
              <SectionTitle>Troubleshooting</SectionTitle>
              
              <FeatureCard>
                <FeatureTitle>How to Remove the RiskMaster Addon from NinjaTrader</FeatureTitle>
                <FeatureContent>
                  <StepCard>
                    <StepTitle>Step 1: Go to Tools → Remove NinjaScript Assembly</StepTitle>
                    <StepImage
                      src="/assets/removeAddon/remove1.jpg"
                      alt="Navigate to Remove NinjaScript Assembly"
                      onClick={() => handleImageClick('/assets/removeAddon/remove1.jpg')}
                    />
                  </StepCard>
                  <StepCard>
                    <StepTitle>Step 2: Select the current version of RiskMaster and AgileDotNet.VMRuntime and press "Remove"</StepTitle>
                    <StepImage
                      src="/assets/removeAddon/remove2.jpg"
                      alt="Select RiskMaster and VMRuntime for Removal"
                      onClick={() => handleImageClick('/assets/removeAddon/remove2.jpg')}
                    />
                  </StepCard>
                  <StepCard>
                    <StepTitle>Step 3: Press "Yes" to confirm removal</StepTitle>
                    <StepImage
                      src="/assets/removeAddon/remove3.jpg"
                      alt="Confirm Removal"
                      onClick={() => handleImageClick('/assets/removeAddon/remove3.jpg')}
                    />
                  </StepCard>
                  <StepCard>
                    <StepTitle>Step 4: Press "Ok" and restart NinjaTrader for the addon to be removed</StepTitle>
                    <StepImage
                      src="/assets/removeAddon/remove4.jpg"
                      alt="Restart NinjaTrader to Finalize Removal"
                      onClick={() => handleImageClick('/assets/removeAddon/remove4.jpg')}
                    />
                  </StepCard>
                </FeatureContent>
              </FeatureCard>
            </SectionCard>

          </ContentArea>
        </MainContent>

        {fullscreenImageSrc && (
          <FullscreenImage onClick={closeFullscreen}>
            <img src={fullscreenImageSrc} alt="Expanded view" />
          </FullscreenImage>
        )}
      </ContentWrapper>
      <Footer />
    </HelpCenterContainer>
  );
};

export default HelpCenter;
